import {
  BookOpen,
  BookOpenText,
  Building,
  CalendarDays,
  ChartLine,
  ClipboardList,
  Clock,
  CreditCard,
  GraduationCap,
  House,
  LayoutDashboard,
  MessageCircle,
  Presentation,
  Settings,
  Ticket,
  Users,
  Activity,
  Library,
  ClipboardCheck,
  CalendarClock,
  Megaphone,
  LifeBuoy,
  ScrollText,
  UserX,
  Headset,
  TableProperties,
  Award,
  ShieldCheck,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import type { Dashboard } from '@shared/lib/roles';

export interface NavContext {
  roles: readonly string[];
  soloMode: boolean;
}

export interface NavItem {
  id: string;
  to: string;
  /** Key in the nav catalog. */
  label: string;
  icon: LucideIcon;
  /** Candidate for the phone bottom navigation (≤ 5 items, UX doc §7). */
  phone?: boolean;
  /** Match only the exact path (dashboard home items). */
  exact?: boolean;
  /** Section heading key rendered above the item in the sidebar. */
  section?: string;
  visible?: (ctx: NavContext) => boolean;
}

const hasAny =
  (...roles: string[]) =>
  (ctx: NavContext) =>
    roles.some((r) => ctx.roles.includes(r));

/** Finance screens are hidden in solo mode (brief D2, OQ-09). */
const financeVisible = (ctx: NavContext) => !ctx.soloMode && hasAny('finance', 'org_admin')(ctx);

/** Role-based navigation trees (UX doc §2 information architecture). */
export const NAVIGATION: Record<Dashboard, readonly NavItem[]> = {
  student: [
    { id: 'student-home', to: '/app/student', label: 'student.home', icon: House, phone: true, exact: true },
    { id: 'student-calendar', to: '/app/student/calendar', label: 'student.calendar', icon: CalendarDays, phone: true },
    { id: 'student-learn', to: '/app/student/learn', label: 'student.learn', icon: GraduationCap, phone: true },
    { id: 'student-progress', to: '/app/student/progress', label: 'student.progress', icon: ChartLine, phone: true },
    { id: 'student-assignments', to: '/app/student/assignments', label: 'student.assignments', icon: ClipboardCheck },
    { id: 'student-exams', to: '/app/student/exams', label: 'student.exams', icon: Award },
    { id: 'student-quran', to: '/app/quran', label: 'common.quran', icon: BookOpenText },
    { id: 'student-messages', to: '/app/messages', label: 'common.messages', icon: MessageCircle },
    { id: 'student-announcements', to: '/app/announcements', label: 'common.announcements', icon: Megaphone },
    { id: 'student-reschedule', to: '/app/reschedule', label: 'common.reschedule', icon: CalendarClock },
    { id: 'student-help', to: '/app/help', label: 'common.help', icon: LifeBuoy },
  ],
  teacher: [
    { id: 'teacher-today', to: '/app/teacher', label: 'teacher.today', icon: Clock, phone: true, exact: true },
    { id: 'teacher-availability', to: '/app/teacher/availability', label: 'teacher.availability', icon: CalendarDays, phone: true },
    { id: 'teacher-students', to: '/app/teacher/students', label: 'teacher.students', icon: Users, phone: true },
    { id: 'teacher-teach', to: '/app/teacher/teach', label: 'teacher.teach', icon: Presentation, phone: true },
    { id: 'teacher-assignments', to: '/app/teacher/assignments', label: 'teacher.assignments', icon: ClipboardCheck },
    { id: 'teacher-exams', to: '/app/teacher/exams', label: 'teacher.exams', icon: Award },
    { id: 'teacher-gradebook', to: '/app/teacher/gradebook', label: 'teacher.gradebook', icon: TableProperties },
    // Solo mode: the independent teacher manages enrollments (and payments) from the enrollment screen.
    { id: 'teacher-enrollments', to: '/app/admin/enrollments', label: 'admin.enrollments', icon: ClipboardList, visible: (ctx) => ctx.soloMode },
    { id: 'teacher-quran', to: '/app/quran', label: 'common.quran', icon: BookOpenText },
    { id: 'teacher-messages', to: '/app/messages', label: 'common.messages', icon: MessageCircle },
    { id: 'teacher-announcements', to: '/app/announcements', label: 'common.announcements', icon: Megaphone },
    { id: 'teacher-reschedule', to: '/app/reschedule', label: 'common.reschedule', icon: CalendarClock },
    { id: 'teacher-help', to: '/app/help', label: 'common.help', icon: LifeBuoy },
  ],
  guardian: [
    { id: 'guardian-home', to: '/app/guardian', label: 'guardian.home', icon: House, phone: true, exact: true },
    { id: 'guardian-calendar', to: '/app/guardian/calendar', label: 'guardian.calendar', icon: CalendarDays, phone: true },
    { id: 'guardian-payments', to: '/app/guardian/payments', label: 'guardian.payments', icon: CreditCard, phone: true },
    { id: 'guardian-messages', to: '/app/messages', label: 'guardian.messages', icon: MessageCircle, phone: true },
    { id: 'guardian-consents', to: '/app/guardian/consents', label: 'guardian.consents', icon: ShieldCheck },
    { id: 'guardian-assignments', to: '/app/guardian/assignments', label: 'guardian.assignments', icon: ClipboardCheck },
    { id: 'guardian-quran', to: '/app/quran', label: 'common.quran', icon: BookOpenText },
    { id: 'guardian-announcements', to: '/app/announcements', label: 'common.announcements', icon: Megaphone },
    { id: 'guardian-reschedule', to: '/app/reschedule', label: 'common.reschedule', icon: CalendarClock },
    { id: 'guardian-help', to: '/app/help', label: 'common.help', icon: LifeBuoy },
  ],
  admin: [
    { id: 'admin-dashboard', to: '/app/admin', label: 'admin.dashboard', icon: LayoutDashboard, phone: true, exact: true },
    {
      id: 'admin-members',
      to: '/app/admin/members',
      label: 'admin.members',
      icon: Users,
      phone: true,
      section: 'admin.sectionPeople',
      visible: hasAny('org_admin', 'coordinator'),
    },
    { id: 'admin-teachers', to: '/app/admin/teachers', label: 'admin.teachers', icon: Presentation, visible: hasAny('org_admin', 'coordinator', 'support') },
    { id: 'admin-students', to: '/app/admin/students', label: 'admin.students', icon: GraduationCap, visible: hasAny('org_admin', 'coordinator', 'support', 'finance') },
    {
      id: 'admin-enrollments',
      to: '/app/admin/enrollments',
      label: 'admin.enrollments',
      icon: ClipboardList,
      phone: true,
      visible: hasAny('org_admin', 'coordinator', 'finance', 'support'),
    },
    {
      id: 'admin-courses',
      to: '/app/admin/courses',
      label: 'admin.courses',
      icon: BookOpen,
      section: 'admin.sectionTeaching',
      visible: hasAny('org_admin', 'coordinator', 'content_manager'),
    },
    { id: 'admin-gradebook', to: '/app/admin/gradebook', label: 'admin.gradebook', icon: TableProperties, visible: hasAny('org_admin', 'coordinator') },
    { id: 'admin-calendar', to: '/app/admin/calendar', label: 'admin.calendar', icon: CalendarDays, visible: hasAny('org_admin', 'coordinator', 'content_manager') },
    { id: 'admin-reschedule', to: '/app/reschedule', label: 'common.reschedule', icon: CalendarClock, visible: hasAny('org_admin', 'coordinator', 'support') },
    {
      id: 'admin-payments',
      to: '/app/admin/finance/payments',
      label: 'admin.payments',
      icon: CreditCard,
      phone: true,
      section: 'admin.sectionFinance',
      visible: financeVisible,
    },
    { id: 'admin-codes', to: '/app/admin/finance/codes', label: 'admin.codes', icon: Ticket, visible: financeVisible },
    { id: 'admin-support', to: '/app/admin/support', label: 'admin.support', icon: Headset, section: 'admin.sectionSupport', visible: hasAny('org_admin', 'coordinator', 'support') },
    { id: 'admin-moderation', to: '/app/admin/moderation', label: 'admin.moderation', icon: ShieldAlert, visible: hasAny('org_admin', 'coordinator', 'support') },
    { id: 'admin-audit', to: '/app/admin/audit', label: 'admin.audit', icon: ScrollText, visible: hasAny('org_admin', 'finance', 'support') },
    { id: 'admin-erasure', to: '/app/admin/erasure', label: 'admin.erasure', icon: UserX, visible: hasAny('org_admin') },
    {
      id: 'admin-messages',
      to: '/app/messages',
      label: 'common.messages',
      icon: MessageCircle,
      section: 'admin.sectionTools',
    },
    { id: 'admin-quran', to: '/app/quran', label: 'common.quran', icon: BookOpenText },
    { id: 'admin-announcements', to: '/app/announcements', label: 'common.announcements', icon: Megaphone },
    { id: 'admin-help', to: '/app/help', label: 'common.help', icon: LifeBuoy },
    {
      id: 'admin-settings',
      to: '/app/admin/settings',
      label: 'admin.settings',
      icon: Settings,
      section: 'admin.sectionOrganization',
      visible: hasAny('org_admin'),
    },
  ],
  'super-admin': [
    { id: 'sa-orgs', to: '/app/platform/organizations', label: 'superAdmin.organizations', icon: Building, phone: true },
    { id: 'sa-users', to: '/app/platform/users', label: 'superAdmin.users', icon: Users, phone: true },
    { id: 'sa-content', to: '/app/platform/content', label: 'superAdmin.content', icon: Library, phone: true },
    { id: 'sa-health', to: '/app/platform/health', label: 'superAdmin.health', icon: Activity, phone: true },
  ],
};

export function visibleNav(dashboard: Dashboard, ctx: NavContext): NavItem[] {
  return NAVIGATION[dashboard].filter((item) => !item.visible || item.visible(ctx));
}

/** Bottom navigation: at most 5 slots; when items overflow, the 5th slot becomes "More". */
export function phoneNav(items: readonly NavItem[]): { primary: NavItem[]; overflow: NavItem[] } {
  const phone = items.filter((i) => i.phone);
  const rest = items.filter((i) => !i.phone);
  const all = [...phone, ...rest];
  if (all.length <= 5) return { primary: all, overflow: [] };
  return { primary: all.slice(0, 4), overflow: all.slice(4) };
}

export function isActivePath(item: NavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.to || pathname === `${item.to}/`;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
